import { ReactComponent as BackIcon } from 'common/assets/images/button-previous.svg';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';

import { mapTitleWithModel } from '../AiSimulationUtil';
import { IFourPromptParamFn } from '../api/type-def';

import {
	useEffect,
	useMemo,
	useState,
	Stack,
	Grid,
	Box,
	Typography,
	_,
	useDispatch,
	useSelector,
	ButtonComp,
	formatSelectValue,
	scrollToTop,
	AiRecipePageTitle,
	AI_TYPE,
	AI_SIMULATION_TYPES,
	setSimulationCommonSaveDrawer,
	setSimulationCommonShareDrawer,
	setControlStepsData,
	HTTP_STATUS_CODES,
	PERMISSION_DENIED_ERROR_MSG,
	setToastMessage,
	GeneratedTextArea,
	StepHeader,
	ControlStatementDropdown,
	ControlTestScriptOutputLayout,
	generatedTextAreaStyles,
	aiModelPDFDownload,
	AIRecipeSave,
	AIShare,
	RootState,
	IAIControlStatementInput,
	IAIRecipeDetails,
	IAIRecipeItem,
	IFourParamFn,
	IOneParamFn,
	aiDefaultStepData,
	toSentenceCase,
	styles,
	useNavigate,
	navigateToRegulationControl,
} from './ObligationAndControlModules';

const { CONTROL_STATEMENT, CONTROL_TEST_SCRIPTS, PLAIN_ENGLISH_SUMMARY } =
	AI_SIMULATION_TYPES;

const { OK, FORBIDDEN } = HTTP_STATUS_CODES;

const ObligationAndControl = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { RECIPE } = AI_TYPE;

	const [isGenerating, setIsGenerating] = useState(false);
	const [currentStep, setCurrentStep] = useState(0);
	const [stepListData, setStepListData] = useState(
		_.cloneDeep(aiDefaultStepData),
	);
	const controlStepsList = useSelector(
		(state: RootState) => state.controlTestScript.controlStepsList,
	);
	const [textData, setTextData] = useState<any>(controlStepsList);

	const regulationText = useSelector(
		(state: RootState) => state.simulationArena.regulationText,
	);

	useEffect(() => {
		currentStepData.inputValue = regulationText;
	}, [regulationText]);

	const currentStepData = useMemo(
		() => stepListData[currentStep],
		[currentStep, stepListData],
	);

	const controlOwnerValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlOwnerValue,
	);
	const controlTypeValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlTypeValue,
	);
	const controlFrequencyValue = useSelector(
		(state: RootState) => state.simulationArena.simulationControlFrequencyValue,
	);
	const workflowFeedback = useSelector(
		(state: RootState) => state.simulationArena.workflowFeedback,
	);
	const plainEnglishSummaryData = useMemo(
		() => stepListData[0].outputValue,
		[stepListData],
	);

	const showOutput = useMemo(
		() => (currentStep === 0 && currentStepData.outputValue) || currentStep > 0,
		[currentStep, stepListData],
	);
	const isFirstStep = currentStep === 0;

	const updateStepListData = (payload: {
		input?: string;
		output?: string;
		targetIndex?: number;
	}) => {
		const { targetIndex = currentStep, input, output } = payload;
		const newData = stepListData.map((item, index) => {
			if (index === targetIndex) {
				if (!_.isNil(input)) {
					item.inputValue = input;
				}
				if (!_.isNil(output)) {
					item.outputValue = output;
				}
			}
			return item;
		});
		setStepListData(newData);
	};

	const handleInputChange = (inputTxt: string) => {
		updateStepListData({ input: inputTxt });
	};

	const handleOutputChange = (outputTxt: string) => {
		updateStepListData({ output: outputTxt });
	};

	const handleBack = () => {
		setCurrentStep((i) => (i > 0 ? i - 1 : i));
	};

	const handleNext = () => {
		if (currentStep + 1 < stepListData.length) {
			setTimeout(scrollToTop);
			// the middle 3 steps' input are all Plain English Summary
			const excludedSteps = ['PlainEnglishSummary', 'ControlStatement'];
			if (excludedSteps.includes(currentStepData.id)) {
				const currentOutput = currentStepData.outputValue;
				updateStepListData({
					input: currentOutput,
					targetIndex: currentStep + 1,
				});
			} else {
				updateStepListData({
					input: plainEnglishSummaryData,
					targetIndex: currentStep + 1,
				});
			}
			setCurrentStep((i) => i + 1);
		}
	};

	const handlePromptSelect = async (event: any) => {
		const input = currentStepData.inputValue;
		const promptKey = event.key;
		if (!input) return;
		setIsGenerating(true);
		let result;
		if (currentStepData.workflowStep === CONTROL_STATEMENT) {
			result = await (
				currentStepData.generatePromptSelect as IFourPromptParamFn
			)(
				input,
				controlFrequencyValue,
				controlTypeValue,
				controlOwnerValue,
				promptKey,
			);
		} else {
			result = await (currentStepData.generatePromptSelect as IOneParamFn)(
				promptKey,
				input,
			);
		}
		if (result.statusCode === OK) {
			handleOutputChange(result.responseBody?.answer as string);
			removeFutureStepsOutput();
		} else if (result.statusCode === FORBIDDEN) {
			dispatch(
				setToastMessage({
					message: PERMISSION_DENIED_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsGenerating(false);
	};

	const handleGenerate = async () => {
		const input = currentStepData.inputValue;
		if (!input) return;
		setIsGenerating(true);
		let result;
		if (currentStepData.workflowStep === CONTROL_STATEMENT) {
			result = await (currentStepData.generatorFn as IFourParamFn)(
				input,
				controlFrequencyValue,
				controlTypeValue,
				controlOwnerValue,
			);
		} else {
			result = await (currentStepData.generatorFn as IOneParamFn)(
				currentStepData.aiModelKey,
				input,
			);
		}
		if (result.statusCode === OK) {
			handleOutputChange(result.responseBody?.answer as string);
			removeFutureStepsOutput();
		} else if (result.statusCode === FORBIDDEN) {
			dispatch(
				setToastMessage({
					message: PERMISSION_DENIED_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		setIsGenerating(false);
	};
	const removeFutureStepsOutput = () => {
		const newListData = stepListData.map((item, index) => {
			if (index > currentStep) {
				return { ...item, outputValue: '' };
			}
			return item;
		});
		setStepListData(newListData);
		const filteredFeedbackList = workflowFeedback.map((ele) => {
			const found = stepListData.some((e, index) => {
				const checkstep =
					index >= currentStep &&
					ele.workflowStep === mapTitleWithModel[e.outputTitle];
				return checkstep;
			});
			if (found) {
				return {
					...ele,
					regenerate: true,
					newFeedback: null,
				};
			}

			return ele;
		});
		dispatch(setWorkflowFeedback(filteredFeedbackList));
	};

	const text = currentStepData.outputValue;
	useEffect(() => {
		const textOnly = text.split(/\d{1,5}\.\s/).filter(Boolean);
		const inputText =
			controlStepsList?.length > 0 ? controlStepsList : textOnly;
		setTextData(inputText);
		dispatch(setControlStepsData(controlStepsList));
	}, [text, controlStepsList]);

	const genText = `${currentStepData.outputValue ? 'Regenerate' : 'Generate'} ${
		currentStepData.outputTitle
	}`;

	const selectedAIRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedAIRecipe,
	);

	const selectedRecipe = useSelector(
		(state: RootState) => state.simulationArena.selectedRecipe,
	);

	const selectedRecipeStatementItem = useMemo(() => {
		if (_.isEmpty(selectedAIRecipe)) return undefined;
		const selectedRecipeStatementInput = selectedAIRecipe.models.filter(
			(model) => model.workflowStep === CONTROL_STATEMENT,
		);
		if (selectedRecipeStatementInput.length === 0) return undefined;
		const inputItems = selectedRecipeStatementInput[0]
			.input as IAIControlStatementInput;
		return {
			controlOwner: inputItems.controlOwner,
			controlFrequency: inputItems.controlFrequency,
			controlType: inputItems.controlType,
		};
	}, [selectedAIRecipe]);

	const generatedOutputText = () => {
		const target = _.isEmpty(controlStepsList) ? textData : controlStepsList;
		const arrWithoutNewLine = (target as Array<any>).map((item, index) => {
			if (index !== target.length - 1) {
				return `${index + 1}. ${item.substring(0, item.length - 1)}`;
			}
			return `${index + 1}. ${item}`;
		});
		return arrWithoutNewLine.join('\n');
	};
	const handleObject = (data: any) => ({
		inputTxt: data.inputValue,
		controlOwner: controlOwnerValue.trim(),
		controlFrequency: formatSelectValue(controlFrequencyValue),
		controlType: formatSelectValue(controlTypeValue),
	});
	const formatRecipeData = () => {
		const res: IAIRecipeItem[] = [];
		stepListData.forEach((data: any) => {
			if (!_.isEmpty(data.inputValue) && !_.isEmpty(data.outputValue)) {
				const inputData =
					data.workflowStep === CONTROL_STATEMENT
						? handleObject(data)
						: data.inputValue;
				const outputData =
					data.workflowStep === CONTROL_TEST_SCRIPTS
						? textData && generatedOutputText()
						: data.outputValue;

				res.push({
					input: inputData,
					output: outputData,
					workflowStep: data.workflowStep,
				});
			}
		});
		return res;
	};

	const fileDownload = () => {
		if (currentStepData.workflowStep === CONTROL_STATEMENT) {
			aiModelPDFDownload(
				_.isEmpty(selectedAIRecipe) ? null : selectedAIRecipe.name,
				`${AiRecipePageTitle} - ${currentStepData?.outputTitle}`,
				AiRecipePageTitle,
				{
					inputTitle: currentStepData?.title,
					inputValue: currentStepData?.inputValue,
				},
				{
					outputTitle: currentStepData?.outputTitle,
					outputValue: currentStepData?.outputValue,
				},
				{
					controlOwner: controlOwnerValue,
					controlFrequency: controlFrequencyValue,
					controlType: controlTypeValue,
				},
			);
		} else {
			aiModelPDFDownload(
				_.isEmpty(selectedAIRecipe) ? null : selectedAIRecipe.name,
				`${AiRecipePageTitle} - ${currentStepData?.outputTitle}`,
				AiRecipePageTitle,
				{
					inputTitle: currentStepData?.title,
					inputValue: currentStepData?.inputValue,
				},
				{
					outputTitle: currentStepData?.outputTitle,
					outputValue:
						currentStepData.workflowStep === CONTROL_TEST_SCRIPTS
							? textData && generatedOutputText()
							: currentStepData?.outputValue,
				},
			);
		}
	};

	const handleSave = () => {
		dispatch(setSimulationCommonSaveDrawer(true));
	};

	const aiRecipeData: IAIRecipeDetails = {
		...(selectedAIRecipe?.id ? { id: selectedAIRecipe.id } : {}),
		name: selectedAIRecipe?.name || '',
		models: formatRecipeData(),
		type: RECIPE,
	};

	const mapAIRecipetoStepListData = () => {
		const recipes = selectedAIRecipe.models;
		const newData = stepListData.map((item) => {
			const existItem = recipes.filter(
				(recipe) => recipe.workflowStep === item.workflowStep,
			);
			if (existItem.length > 0) {
				const selectedEl = existItem[0];
				item.workflowStep = selectedEl.workflowStep as AI_SIMULATION_TYPES;
				item.inputValue =
					selectedEl.workflowStep === CONTROL_STATEMENT
						? (selectedEl.input as IAIControlStatementInput).inputTxt
						: (selectedEl.input as string);
				item.outputValue = selectedEl.output as string;
			}
			return item;
		});
		setStepListData(newData);
	};

	useEffect(() => {
		if (!_.isEmpty(selectedAIRecipe)) {
			mapAIRecipetoStepListData();
			setCurrentStep(selectedAIRecipe.models.length - 1);
		}
	}, [selectedAIRecipe]);

	const handleShare = () => {
		dispatch(setSimulationCommonShareDrawer(true));
	};

	const backButtonHandler = () => {
		dispatch(setWorkflowFeedback([]));
		navigateToRegulationControl(navigate, regulationText);
	};
	const shouldDelete =
		selectedAIRecipe.id && currentStepData.outputValue ? isGenerating : false;
	const outputActionClassName = currentStepData.outputValue
		? ''
		: generatedTextAreaStyles.disabled;

	return (
		<>
			<Stack>
				<StepHeader
					showStepbar
					title="Regulation obligations and control generation"
					currentStepIndex={currentStep}
				/>
				{currentStepData.workflowStep === PLAIN_ENGLISH_SUMMARY &&
					selectedRecipe && (
						<Grid container direction="row" display="flex">
							<Box className={styles['back-icon']} onClick={backButtonHandler}>
								<BackIcon />
							</Box>
							<Typography
								className={styles['back-button-text']}
								onClick={backButtonHandler}
							>
								Back to Search Database
							</Typography>
						</Grid>
					)}
				<Stack gap={3}>
					{/* input */}
					<GeneratedTextArea
						category={RECIPE}
						title={toSentenceCase(currentStepData.title)}
						onChange={handleInputChange}
						button={{
							text: genText,
							aiModelKey: currentStepData.aiModelKey,
							type: currentStepData.outputValue ? 'tertiary' : 'primary',
							loading: isGenerating,
							onPromptChange: handlePromptSelect,
							onClick: handleGenerate,
						}}
						textValue={currentStepData.inputValue}
						inputElement={
							currentStepData.workflowStep === CONTROL_STATEMENT ? (
								<ControlStatementDropdown items={selectedRecipeStatementItem} />
							) : (
								''
							)
						}
					/>

					{/* out */}
					{showOutput &&
						(currentStepData.workflowStep === CONTROL_TEST_SCRIPTS ? (
							<ControlTestScriptOutputLayout
								category={RECIPE}
								inputData={currentStepData.inputValue}
								outputResult={currentStepData.outputValue}
								testScriptSteps={textData}
								outputActionClassName={outputActionClassName}
								downloadHandler={fileDownload}
								isGenerating={shouldDelete}
								shareHandler={handleShare}
							/>
						) : (
							<GeneratedTextArea
								category={RECIPE}
								inputData={currentStepData.inputValue}
								title={currentStepData.outputTitle}
								dataType="output"
								editable
								onChange={handleOutputChange}
								textValue={currentStepData.outputValue}
								isSaveIconVisible
								downloadHandler={fileDownload}
								saveHandler={handleSave}
								shareHandler={handleShare}
								isRegenerating={isGenerating}
								isIconDisable={
									!(currentStepData.inputValue && currentStepData.outputValue)
								}
							/>
						))}

					<Stack direction="row" justifyContent="flex-end">
						<Stack direction="row" gap={2}>
							{!isFirstStep && (
								<ButtonComp
									btnType="secondary"
									size="small"
									clickHandler={handleBack}
								>
									Back
								</ButtonComp>
							)}
							{showOutput && currentStep !== aiDefaultStepData.length - 1 && (
								<ButtonComp
									btnType="primary"
									size="small"
									clickHandler={handleNext}
									disabled={!currentStepData.outputValue}
								>
									Next
								</ButtonComp>
							)}
							<AIRecipeSave
								aiRecipeData={aiRecipeData}
								isSaveBtnVisible={currentStep === aiDefaultStepData.length - 1}
								isSaveBtnDisable={
									currentStep !== aiRecipeData.models.length - 1
								}
							/>
						</Stack>
					</Stack>
				</Stack>
			</Stack>
			<AIShare aiSharedData={aiRecipeData} type={RECIPE} />
		</>
	);
};

export default ObligationAndControl;
